import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@hub/common/Modal/Modal';
import type { LanguageAlternate } from '@hub/models/data';
import { locales } from '@icommerce/i18n';
import type { LocaleItem, ContinentItem, CountryItem } from '@icommerce/i18n';
import { useTranslation } from 'next-i18next';
import { Button, TYPE } from '@hub/common/Button/Button';
import { Link } from '@hub/common/Link/Link';
import { useLocaleSelectionContext } from '@hub/hooks/useLocaleSelectionContext';
import Cookies from 'js-cookie';
import { Dropdown } from '@hub/common/Dropdown/Dropdown';
import type { GroupedDropdownValues } from '@hub/common/Dropdown/Dropdown';
import { useRouter } from 'next/router';
import { useLocaleSelection } from '@hub/hooks/useLocaleSelection';
import type { LocaleSelectionProps } from '@hub/hooks/useLocaleSelection';
import { getLocaleItemForLocaleString } from '@hub/libs/i18n.helper';
import { IGUS_LOCALE_COOKIE_NAME } from '@hub/libs/constants';

const continents: ContinentItem[] = locales as ContinentItem[];

/**
 *
 * Removes the session cookie before switching from es or en to different .eu domain language.
 * The removal of these cookies is only required when we are on the EU domain!
 * domain and the target language alternate uses the EU domain/host too and the target language is part of the current
 * language.
 * @param alternate The target language alternate
 */
export function removeCookiesOnSameEuDomain(
  targetAlternate: LanguageAlternate,
  targetLocale: LocaleItem,
  currentLanguage: string
) {
  if (!targetAlternate) {
    return;
  }
  let targetDomain = window.location.hostname;
  if (targetDomain.startsWith('www')) {
    targetDomain = targetDomain.slice(3);
  }

  const isTargetDomainSameAsCurrent = targetAlternate.href.indexOf(targetDomain) >= 0;
  const isTheSwitchBetweenEnAndES =
    (currentLanguage === 'en' && targetLocale.code === 'es') ||
    (currentLanguage === 'es' && targetLocale.code === 'en');
  if (
    window.location.hostname.match(/.igus.eu($|\.)/) &&
    isTargetDomainSameAsCurrent &&
    currentLanguage !== targetLocale.code &&
    !isTheSwitchBetweenEnAndES
  ) {
    Cookies.remove('server');
    Cookies.remove('Global_SessionId');
  }
}

export const LocaleSelectionModal = ({ languageAlternates, currentProduct }: LocaleSelectionProps) => {
  const { i18n, t } = useTranslation('layout/localeSelection');
  const { toggleLocaleSelectionVisibility, localeSelectionVisible } = useLocaleSelectionContext();
  const { locale: routerLocale } = useRouter();
  const [showWarning, setShowWarning] = useState(false);
  const { getCurrentUrlForTargetLocale } = useLocaleSelection({ languageAlternates, currentProduct });
  const [selectedCountry, setSelectedCountry] = useState('');

  const currentCountry: CountryItem = continents
    .flatMap(continent => continent.countries)
    .filter(country => country.languages.some(language => language.code === i18n.language))[0];

  useEffect(() => {
    setSelectedCountry(currentCountry?.name);
  }, [currentCountry]);

  const [warningLocale, setWarningLocale] = useState<LocaleItem>();
  const internationalCountryName = 'International';

  const selectedCountryLocales = useMemo(
    () =>
      continents.flatMap(continent => continent.countries).filter(country => country.name === selectedCountry)[0]
        ?.languages,
    [selectedCountry, continents]
  );

  const handleMissingLocaleConfirm = (locale: LocaleItem | undefined) => {
    if (locale) {
      window.location.href = getCurrentUrlForTargetLocale(locale);
    }

    setShowWarning(false);
    toggleLocaleSelectionVisibility();
  };

  // FIXME: replace confirm dialog with a nicer alternative
  const handleLocaleClick = (e: React.MouseEvent, locale: LocaleItem) => {
    Cookies.set(IGUS_LOCALE_COOKIE_NAME, locale.languageCode);
    if (locale.code === i18n.language) {
      e.preventDefault();
      toggleLocaleSelectionVisibility();
      return;
    }
    const alternate = languageAlternates.find(languageAlternate => languageAlternate.hrefLang === locale.code);
    if (alternate) {
      removeCookiesOnSameEuDomain(alternate, locale, i18n.language);
      toggleLocaleSelectionVisibility();
      return;
    }
    e.preventDefault();
    setWarningLocale(locale);
    setShowWarning(true);
  };

  const renderLocaleLink = (language: LocaleItem) => (
    <Link
      key={language.code}
      text={language.title}
      primary
      className="mt-[5px]"
      href={getCurrentUrlForTargetLocale(language)}
      onClick={e => {
        handleLocaleClick(e, language);
      }}
    />
  );

  return (
    <>
      <Modal
        visible={localeSelectionVisible}
        onClose={() => {
          toggleLocaleSelectionVisibility();
        }}
      >
        <div className="my-10 w-7xl max-w-7xl flex flex-wrap text-gray-700">
          <div className="block w-1/2 pr-7">
            <h2>{t('LOCALE_SELECTION.COUNTRY_REGION_TITLE')}</h2>
            <div className="text-base font-normal mt-5">
              <div className="block mb-[20px]">{t('LOCALE_SELECTION.COUNTRY_REGION_DESCRIPTION')}</div>
              <Dropdown
                id="language-selector"
                testidSuffix="language-selector"
                onChange={val => {
                  setSelectedCountry(val);
                }}
                selected={selectedCountry}
                values={continents.map(continent => {
                  const countries = continent.countries
                    .map(country => [
                      country.altname !== undefined ? `${country.name} - ${country.altname}` : country.name,
                      country.name,
                    ])
                    .concat([['Other', 'Other']]) as [string, string][];
                  return {
                    group: continent.title,
                    values: new Map(countries),
                  } as GroupedDropdownValues;
                })}
              />
            </div>
          </div>
          <div className="block w-1/2 pl-7">
            <h2>{t('LOCALE_SELECTION.LANGUAGE_TITLE')}</h2>
            <div className="text-base font-normal mt-5">
              <div className="block mb-[20px]">{t('LOCALE_SELECTION.LANGUAGE_DESCRIPTION')}</div>
              <span data-testid="language-selection-country-links" className="w-full">
                {selectedCountryLocales?.length > 0 &&
                  selectedCountry !== internationalCountryName &&
                  selectedCountryLocales.map(language => renderLocaleLink(language))}
                {!selectedCountryLocales?.length || selectedCountry === internationalCountryName ? (
                  <div>
                    {/* as of now, the following text shall not be translated - it should always be English! */}
                    {routerLocale !== 'fr' && (
                      <span className="block">please visit one of our international pages below.</span>
                    )}
                    <hr className="my-[15px] w-full h-4" />
                    {continents
                      .flatMap(continent => continent.countries)
                      .find(country => country.name === internationalCountryName)
                      ?.languages.map(language => renderLocaleLink(language))}
                  </div>
                ) : (
                  <div>
                    <hr className="my-[15px] w-full h-4" />
                    {continents
                      .flatMap(continent => continent.countries)
                      .find(country => country.name === internationalCountryName)
                      ?.languages.filter(language => ['en', 'es', 'fr'].includes(language.code))
                      .map(language => renderLocaleLink(language))}
                  </div>
                )}
              </span>
            </div>
          </div>
        </div>
      </Modal>
      <Modal visible={showWarning} onClose={() => setShowWarning(false)} headline={t('LOCALE_SELECTION.MISSING_TITLE')}>
        <div data-testid="locale-selection-warning">
          <div className="text-2xl mb-10 whitespace-pre-line" data-testid="locale-selection-warning-text">
            {t('LOCALE_SELECTION.MISSING_TEXT', { locale: warningLocale?.title ?? 'this location' })}
          </div>
          <Button
            text={t('LOCALE_SELECTION.MISSING_CANCEL')}
            type={TYPE.SECONDARY}
            onClick={() => setShowWarning(false)}
          />
          <span className="inline-block ml-12" />
          <Button
            text={t('LOCALE_SELECTION.MISSING_CONFIRM')}
            testId="locale-alternate-missing-warning-button"
            type={TYPE.PRIMARY}
            onClick={() => handleMissingLocaleConfirm(warningLocale)}
          />
        </div>
      </Modal>
    </>
  );
};

export const LocaleSelection = (props: LocaleSelectionProps) => {
  const { i18n } = useTranslation('layout/localeSelection');
  const { toggleLocaleSelectionVisibility } = useLocaleSelectionContext();

  const currentLocale = getLocaleItemForLocaleString(i18n.language);

  return (
    <div className="flex-none" data-testid="locale-selection">
      <span
        data-testid="locale-selection-current"
        className="pointer text-white hover:font-bold cursor-pointer"
        onClick={() => {
          toggleLocaleSelectionVisibility();
        }}
      >
        {currentLocale ? currentLocale.short : 'UNKNOWN'}
      </span>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <LocaleSelectionModal {...props} />
    </div>
  );
};
